import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { SubscriptionField } from "./SubscriptionField";
import { VipField } from "./VipField";
import { PlanField } from "./PlanField";
import { TransactionsField } from "./TransactionsField";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import ProfilesService from "@/features/profiles/services/profiles/profileService";
import { IUserById, IUserUpdatePayload, UserRole, EmployeeRole } from "@/features/profiles/services/profiles/types";
import { z } from "zod";
import { AxiosError } from "axios";
import { ModalDeleteCreatorWarning } from "./ModalDeleteCreatorWarning";
import { Select } from "@/shared/widgets/select";
import { useToast } from "@/shared/hooks/use-toast";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import CreatorService from "@/features/creators/services/creatorService";

const userEditSchema = z
  .object({
    email: z.string().email("E-mail inválido"),
    username: z.string().optional(),
    isCreator: z.boolean().default(false),
    motto: z.string().optional(),
    dynamicLink: z.string().optional(),
    vip: z.boolean(),
    vipExpiresAt: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.vip && !data.vipExpiresAt) {
      ctx.addIssue({
        path: ["vipExpiresAt"],
        code: z.ZodIssueCode.custom,
        message: "Data de expiração do VIP obrigatória",
      });
    }
  });

export default function UserEdit() {
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [isCreator, setIsCreator] = useState(false);
  const [wasCreator, setWasCreator] = useState(false); // 👈 guarda estado inicial
  const [motto, setMotto] = useState("");
  const [dynamicLink, setDynamicLink] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [vip, setVip] = useState(false);
  const [vipExpiresAt, setVipExpiresAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole>("USER");
  const [employeeRole, setEmployeeRole] = useState<EmployeeRole | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [creatorId, setCreatorId] = useState<string>("");

  useEffect(() => {
    return () => {
      if (avatarPreview && avatarPreview.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
      if (coverPreview && coverPreview.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    };
  }, []);

  const { mutate: createOrUpdateProfile, isPending } = useCustomMutation({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.createOrUpdateProfile,
    onSuccess: () => {
      toast({
        title: "Perfil atualizado",
        description: "As alterações foram salvas com sucesso.",
      });
      queryClient.invalidateQueries({ queryKey: ["get-profile-by-id"] });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast({
        title: "Erro ao atualizar perfil",
        description: error.response?.data.message ?? "Erro ao atualizar perfil",
      });
    },
  });

  const { data: fetchedUser, isLoading: isFetching } = useCustomQuery({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.getProfileById,
    parameter: { id: id as string },
    options: { enabled: Boolean(id) },
  });

  const { data: creatorPacks, refetch: refetchCreatorPacks } = useCustomQuery({
    serviceInstance: CreatorService.Instance,
    selectAction: (s) => s.getPacksByCreator,
    parameter: { id: creatorId },
    options: { enabled: false },
  });



  useEffect(() => {
    if (!fetchedUser) return;

    setEmail((fetchedUser as any).email ?? "");
    setVip(Boolean(fetchedUser.vip));
    setVipExpiresAt(fetchedUser.vipExpiresAt ? new Date(fetchedUser.vipExpiresAt) : null);

    const subscription: IUserById["subscription"] = fetchedUser.subscription;
    setSubscribed(Boolean(subscription?.active));
    setExpiresAt(subscription?.expiresAt ? new Date(subscription.expiresAt) : null);

    const creatorData = fetchedUser.creatorData;
    const initialCreator = Boolean(creatorData?.isActive);
    setIsCreator(initialCreator);
    setWasCreator(initialCreator); // 👈 salva estado inicial
    setCreatorId(creatorData?.packsCreators?.[0]?.creatorId ?? "");
    setName((creatorData as any)?.name ?? "");
    setUsername(creatorData?.username ?? "");
    setRole(creatorData?.role ?? "USER");
    setEmployeeRole((fetchedUser as any).employees?.roles?.[0] ?? null);

    const translations = creatorData?.translations as Array<any> | undefined;
    const pt = translations?.find((t) => t.languageCode === "PT_BR");
    setMotto(pt?.motto ?? translations?.[0]?.motto ?? "");
    setDynamicLink(creatorData?.promoLinks?.[0]?.url ?? "");

    const sanitize = (v?: string | null) => {
      if (!v) return null;
      const s = String(v).trim();
      if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return null;
      return s;
    };
    setAvatarPreview(sanitize(creatorData?.avatar));
    setCoverPreview(sanitize(creatorData?.cover));
  }, [fetchedUser]);

  const handleDisableCreator = async () => {
    const { data } = await refetchCreatorPacks(); // 👈 busca só agora

    if (data && data.length > 0) {
      setShowDeleteUserModal(true);
      return;
    }

    setIsCreator(false);
  };
  const handleSave = async () => {
    // 👇 bloqueia desativação se houver packs ativos
    if (wasCreator && !isCreator) {
      if (creatorPacks && creatorPacks.length > 0) {
        setShowDeleteUserModal(true);
        return;
      }
    }

    const newUser = {
      email,
      username,
      isCreator,
      motto,
      dynamicLink,
      vip,
      role: role as UserRole,
      vipExpiresAt: vipExpiresAt?.toISOString() ?? null,
      subscription: {
        active: subscribed,
        expiresAt,
      },
    };

    const parsed = userEditSchema.safeParse(newUser);
    if (!parsed.success) {
      const fieldErrors = parsed.error.formErrors.fieldErrors;
      const formattedErrors: Record<string, string> = {};
      for (const key in fieldErrors) {
        if (fieldErrors[key]?.length) {
          formattedErrors[key] = fieldErrors[key]![0];
        }
      }
      setFormErrors(formattedErrors);
      return;
    }

    setFormErrors({});
    setIsLoading(true);

    const creatorPayload: IUserUpdatePayload = {
      email,
      data: {
        motto,
        dynamicLink,
        name,
        username,
        isCreator,
        role,
        vip,
        vipExpiresAt: vipExpiresAt?.toISOString() ?? null,
        employeeRole,
      },
    };
    if (avatarFile) creatorPayload.avatar = avatarFile;
    if (coverFile) creatorPayload.cover = coverFile;

    createOrUpdateProfile(creatorPayload);
    setIsLoading(false);
  };

  const handleBack = () => {
    const prevPageUrl = location.state?.from;
    navigate(prevPageUrl || "/users");
  };

  useEffect(() => {
    if (vip && !vipExpiresAt) {
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 100);
      setVipExpiresAt(futureDate);
    }
  }, [vip, vipExpiresAt]);

  useEffect(() => {
    if (subscribed && !expiresAt) {
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 1);
      setExpiresAt(futureDate);
    }
  }, [subscribed, expiresAt]);



  return (
    <PageContent title="Editar Usuário" items={[
      {
        label: "Usuários",
        href: "/users",
      },
      {
        label: "Editar Usuário",
        href: "/users/edit",
      },
    ]}>
      {(isLoading || isFetching) && <p className="text-white mb-4">Carregando...</p>}
      <ModalDeleteCreatorWarning
        isOpen={showDeleteUserModal && creatorPacks?.length > 0}
        onClose={() => setShowDeleteUserModal(false)}
        pendingItems={creatorPacks ?? []}
      />

      <div className="bg-[#222222] p-8 rounded-lg border border-[#333333] space-y-8">
        <fieldset className="border border-[#333333] rounded-lg p-0 overflow-hidden">
          <legend className="px-2 text-lg font-semibold text-white">Perfil</legend>
          <div className="mt-4 ">
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <div className="relative h-56 md:h-64 lg:h-72 w-full bg-black/30">
                {coverPreview ? (
                  <img
                    src={coverPreview}
                    alt="Capa"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={() => setCoverPreview(null)}
                  />
                ) : (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 cursor-pointer"
                    onClick={() => coverInputRef.current?.click()}
                    aria-label="Adicionar capa"
                    title="Adicionar capa"
                  >
                    <div className="absolute inset-3 rounded-xl border-2 border-dashed border-white/20" />
                    <span className="text-xs">Sem foto</span>                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <button
                  type="button"
                  className="absolute bottom-4 right-4 px-3.5 py-1.5 rounded-full text-sm bg-black/50 border border-white/20 text-white hover:bg-black/60 backdrop-blur-sm"
                  onClick={() => coverInputRef.current?.click()}
                >{coverPreview ? "Alterar capa" : "Adicionar capa"}</button>
              </div>

              {/* Avatar fixo no canto inferior esquerdo */}
              <div className="absolute left-6 bottom-6">
                <div className="relative w-36 h-36 md:w-44 md:h-44 lg:w-48 lg:h-48 rounded-full overflow-hidden ring-2 ring-white/30 border border-white/20 bg-black/80 shadow-xl">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      className="w-full h-full object-cover bg-black"
                      onError={() => setAvatarPreview(null)}
                    />
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center text-white/80 cursor-pointer"
                      onClick={() => avatarInputRef.current?.click()}
                      aria-label="Adicionar foto"
                      title="Adicionar foto"
                    >
                      <span className="text-3xl leading-none">+</span>
                      <span className="text-xs">Sem foto</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3.5 py-1 rounded-full text-xs bg-black/60 border border-white/20 text-white hover:bg-black/70 backdrop-blur-sm flex items-center justify-center"
                    onClick={() => avatarInputRef.current?.click()}
                  >{avatarPreview ? "Alterar" : "Adicionar"}</button>
                </div>
              </div>
            </div>

            <div className="h-12" />


            <input
              ref={coverInputRef}
              id="cover"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setCoverFile(file);
                setCoverPreview(file ? URL.createObjectURL(file) : coverPreview);
              }}
            />
            <input
              ref={avatarInputRef}
              id="avatar"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setAvatarFile(file);
                setAvatarPreview(file ? URL.createObjectURL(file) : avatarPreview);
              }}
            />
          </div>
        </fieldset>
        <fieldset className="border border-[#333333] rounded-lg p-6">
          <legend className="px-2 text-lg font-semibold text-white">Dados pessoais</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white text-base">
                E-mail
              </Label>
              <Input
                id="email"
                readOnly
                placeholder={email}
                className="bg-[#1A1A1A] opacity-50 cursor-not-allowed border-[#333333] text-white h-10 max-w-[320px]"
              />
              {formErrors.email && (
                <p className="text-sm text-red-500">{formErrors.email}</p>
              )}
            </div>
          </div>
        </fieldset>

        <fieldset className="border border-[#333333] rounded-lg p-6">
          <legend className="px-2 text-lg font-semibold text-white">Assinatura e VIP</legend>
          <div className="space-y-6 mt-4">
            <SubscriptionField
              formErrors={formErrors}
              subscribed={subscribed}
              onSubscribedChange={setSubscribed}
              expiresAt={expiresAt}
              onExpiresAtChange={setExpiresAt}
            />

            <PlanField plan={fetchedUser?.subscription?.plan} />

            <VipField
              formErrors={formErrors}
              vip={vip}
              onVipChange={setVip}
              vipExpiresAt={vipExpiresAt}
              onVipExpiresAtChange={setVipExpiresAt}
            />
          </div>
        </fieldset>

        <fieldset className="border border-[#333333] rounded-lg p-6">
          <legend className="px-2 text-lg font-semibold text-white">Perfil de Criador</legend>
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between py-2 border border-[#333333] rounded-md px-4 bg-[#1F1F1F]">
              <div>
                <Label htmlFor="isCreator" className="text-white text-base">
                  Habilitar perfil de criador
                </Label>
                <p className="text-xs text-white/60">Acesso a recursos de criador</p>
              </div>
              <Switch
                id="isCreator"
                checked={isCreator}
                onCheckedChange={(checked) => {
                  if (!checked) {
                    handleDisableCreator();
                  } else {
                    setIsCreator(true);
                  }
                }}
                className="data-[state=checked]:bg-[#C1174F]"
              />
            </div>

            {isCreator && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <Label htmlFor="name" className="text-white text-base">
                    Nome
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    placeholder="Seu nome"
                    onChange={(e) => setName(e.target.value)}
                    className="bg-[#1A1A1A] border-[#333333] text-white h-10 w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="username" className="text-white text-base">
                    Username
                  </Label>
                  <Input
                    id="username"
                    value={username}
                    placeholder="Username"
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-[#1A1A1A] border-[#333333] text-white h-10 w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="role" className="text-white text-base">
                    Perfil do criador
                  </Label>
                  <Select
                    placeholder="Selecione um perfil"
                    value={role}
                    onChange={(e) => setRole(e as UserRole)}
                    data={[
                      { value: "INFLUENCER", label: "Influenciador" },
                      { value: "PHOTOGRAPHER", label: "Fotógrafo" },
                    ]}
                  />
                </div>
                <div>
                  <Label htmlFor="motto" className="text-white text-base">
                    Sobre
                  </Label>
                  <Input
                    id="motto"
                    value={motto}
                    onChange={(e) => setMotto(e.target.value)}
                    className="bg-[#1A1A1A] border-[#333333] text-white h-10 w-full"
                    placeholder="Frase curta sobre você"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="dynamicLink" className="text-white text-base">
                    Link personalizado
                  </Label>
                  <Input
                    id="dynamicLink"
                    value={dynamicLink}
                    onChange={(e) => setDynamicLink(e.target.value)}
                    className="bg-[#1A1A1A] border-[#333333] text-white h-10 w-full"
                    placeholder="https://seulink.com/usuario"
                  />
                </div>
              </div>
            )}
          </div>
        </fieldset>

        <fieldset className="border border-[#333333] rounded-lg p-6">
          <legend className="px-2 text-lg font-semibold text-white">Perfil de Funcionário</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <Label htmlFor="employeeRole" className="text-white text-base">
                Cargo do funcionário
              </Label>
              <Select
                placeholder="Selecione um cargo"
                value={employeeRole ?? "NONE"}
                onChange={(e) => setEmployeeRole(e === "NONE" ? null : (e as EmployeeRole))}
                data={[
                  { value: "NONE", label: "Nenhum" },
                  { value: "ADMIN", label: "Administrador" },
                  { value: "SUPPORT", label: "Suporte" },
                  { value: "FINANCIAL", label: "Financeiro" },
                  { value: "READ_ONLY", label: "Somente Leitura" },
                  { value: "MARKETING", label: "Marketing" },
                ]}
              />
            </div>
          </div>
        </fieldset>

        {/* Seção de Transações */}
        {fetchedUser?.transactions && fetchedUser.transactions.length > 0 && (
          <TransactionsField transactions={fetchedUser.transactions} />
        )}

        <div className="flex justify-end pt-4 gap-4">
          <Button
            variant="outline"
            className="bg-transparent hover:bg-[#333333] text-white border-[#333333]"
            onClick={handleBack}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            className="bg-[#C1174F] hover:bg-[#A01441] text-white"
          >
            {isPending ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>
    </PageContent>
  );
}
